import appConfig from "@config/app-config.json";
import { CredentialsAndIdentityId, fetchAuthSession } from "aws-amplify/auth";
import React, { useContext, useMemo } from "react";
import {
	VelavuEventStreamConfig,
	VelavuEventStreamConfigProvider,
	VelavuEventStreamCredentialsProvider,
} from "velavu-js-api";
import { VelavuStreamContextProvider } from "velavu-react";
import { useSignInContext } from "../sign-in-context/sign-in-context";

type AWSCredentials = CredentialsAndIdentityId["credentials"];

/// An implementation of the credentials provider for AWS Cognito
class AWSCognitoCredentialsProvider extends VelavuEventStreamCredentialsProvider {
	#cachedCredentials: AWSCredentials;

	constructor(credentials: AWSCredentials) {
		super();
		this.#cachedCredentials = credentials;
	}

	async refreshCredentials() {
		const authSession = await fetchAuthSession();
		if (authSession.credentials === undefined) {
			throw new Error(
				"Failed to refresh AWS credentials; credentials are undefined",
			);
		}
		this.#cachedCredentials = authSession.credentials;
	}

	getCredentials() {
		return {
			aws_region: appConfig.region,
			aws_access_id: this.#cachedCredentials.accessKeyId,
			aws_secret_key: this.#cachedCredentials.secretAccessKey,
			aws_sts_token: this.#cachedCredentials.sessionToken,
		};
	}
}

export interface StreamProviderProps {
	children?: React.ReactNode;
}

/// A React context provider for the Velavu event stream
export default function StreamProvider(props: StreamProviderProps) {
	const userContext = useSignInContext();

	const customerID = userContext.organization?.customer_id;
	const configProvider = useMemo(():
		VelavuEventStreamConfigProvider | undefined => {
		//Ignoring if data isn't loaded
		if (customerID === undefined) return;

		//Create the stream config provider
		return async (): Promise<VelavuEventStreamConfig> => {
			const authSession = await fetchAuthSession();
			if (authSession.identityId === undefined) {
				throw new Error(
					"Failed to connect to MQTT; identity ID is undefined",
				);
			}
			if (authSession.credentials === undefined) {
				throw new Error(
					"Failed to connect to MQTT; credentials are undefined",
				);
			}

			return {
				mqttEndpoint: appConfig.mqttEndpoint,
				awsIdentityID: authSession.identityId,
				customerID: customerID,
				credentialsProvider: new AWSCognitoCredentialsProvider(
					authSession.credentials,
				),
				awsRegion: appConfig.region,
			};
		};
	}, [customerID]);

	return (
		<VelavuStreamContextProvider configProvider={configProvider}>
			{props.children}
		</VelavuStreamContextProvider>
	);
}
